import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Box } from '@99designs/emu';
import SiteFooter from '@99designs/site-navigation/components/SiteFooter/SiteFooter';
import { Site } from '@99designs/site-navigation/lib/Site';
import { trackEvent } from '@99designs/tracking';
import { FadeTransition } from '../../components/FadeTransition';
import { HeaderNav } from '../../components/Onboarder/HeaderNav/HeaderNav';
import { useOnboarder } from '../../components/Onboarder/OnboarderContext';
import { TransitionState, useRouteTransition } from '../../components/RouteTransitionProvider';
import { Support } from '../../components/Support';
import { recommend } from '../RecommendationEngine';
import { ONBOARDER_NAME } from '../eventTracking';
import { Recommendations } from './Recommendations';
import { usePackageConfig } from './usePackageConfig';
import type { Role } from '@99designs/site-navigation/consts';
import type { Data } from '../config';

interface Props {
    role: Role;
    site: Site;
}

const RecommendationsContainer = styled(Box)`
    overflow: hidden;
`;

export function RecommendationPage({ role, site }: Props) {
    const locale = site.siteConfig.locale;
    const { data, config } = useOnboarder<Data>();
    const products = usePackageConfig();
    const recommendations = recommend(data, locale, products);

    const { transitionState, enableTransition } = useRouteTransition();

    useEffect(() => {
        return enableTransition(500);
    });

    useEffect(() => {
        if (transitionState === TransitionState.Enter) {
            trackEvent('Viewed Onboarder Recommendations', {
                onboarder_name: ONBOARDER_NAME,
                recommendations: recommendations.map((r) => r.key).join(', '),
            });
        }
    }, [recommendations, transitionState]);

    return (
        <>
            <HeaderNav userRole={role} />
            <FadeTransition state={transitionState}>
                <RecommendationsContainer
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    width="100%"
                >
                    {products.length > 0 && (
                        <>
                            <Box
                                display="flex"
                                flexDirection="column"
                                width="100%"
                                maxWidth="1300px"
                                alignItems={'left'}
                                marginTop="4bu"
                                paddingX="2bu"
                            >
                                <Recommendations
                                    recommendations={recommendations}
                                    currency={config.currency}
                                    exchangeRate={config.exchangeRate}
                                />
                            </Box>
                            {/* Hack in some extra space. */}
                            <Box marginTop={['none', '4bu']} />
                            <Box marginTop={['3bu', '8bu']} paddingX="2bu" maxWidth="590px">
                                <Support site={site} />
                            </Box>
                            <Box marginTop="8bu" width="100%">
                                <SiteFooter isFocused site={site} />
                            </Box>
                        </>
                    )}
                </RecommendationsContainer>
            </FadeTransition>
        </>
    );
}
